import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { resolveDoodad, hashId } from "@tribez/shared";
import { type AssetBundle, type NatureKey } from "../assets";
import { toToon, applyToonShader, bakeBaseAO, occludesHero } from "../toon";
import { VERT_DIM, MAP_UNITS } from "./constants";
import { skirtDrop } from "./terrain";
import type { ObjectRow } from "./types";

// Resource nodes (MapleTree / StoneDeposit / BerryBush and their swap variants) are
// rendered from server state via resources.ts — per-match conversion + HP need server
// authority. world.ts only handles static doodads + landmark units (mammoth).
//
// Visual variety comes from `world-doodads.ts`'s `DOODAD_REGISTRY` — a per-KTS-type
// map to one or more `NatureKey` variants. This module only handles three things:
//  (1) the mammoth landmark (TheOldMammoth → Bull mesh, hand-tuned scale),
//  (2) the per-shape bucket fallback for KTS doodad types not in the registry,
//  (3) instancing + matrix construction for both paths.
//
// World convention: 1 unit = 1 meter. KTS UnitData.xml gives gameplay radii; bucket
// fallback scales below match Phase 1 coverage (Pine_3 / Pebble_Round_3 / Grass_Common_Tall
// / Mushroom_Common). Registered entries override these with their own `scale` field.

type FallbackBucket = "doodadTree" | "doodadRock" | "doodadPlant" | "doodadOther";

type FallbackDef = {
  variant: NatureKey;
  scale: number;
};

const FALLBACK_BUCKETS: Record<FallbackBucket, FallbackDef> = {
  doodadTree:  { variant: "pine3",          scale: 1.14 },
  doodadRock:  { variant: "pebbleRound3",   scale: 3.0  },
  doodadPlant: { variant: "grassTall",      scale: 0.32 },
  doodadOther: { variant: "mushroomCommon", scale: 1.09 },
};

// Resource node units (MapleTree/StoneDeposit/BerryBush) are excluded — server owns them
// via state.resources, rendered in resources.ts. Resurrection shrines stay as placeholder
// mushrooms via the doodadOther fallback.
const RESOURCE_NODE_TYPES = /^(MapleTree|StoneDeposit|BerryBush)$/;

// Decorative doodad types that render the SAME bushCommon/bushCommonFlowers mesh
// as the harvestable BerryBush/CulinaryHerbBush resource nodes (see
// shared/doodad-registry.ts). At ~370 instances they vastly outnumber the ~75 real
// berry bushes and are visually indistinguishable, so players couldn't tell which
// bush was harvestable (no tooltip / no harvest on the decorative ones). Hidden for
// now (Aviv, 2026-06-17) so EVERY bush in the world is a real harvestable node;
// restore when these get their own distinct art. Purely visual — the server already
// treats all of these as walk-through (static-obstacles.ts doodadObstacleRadius →
// null for non-tree/rock), so hiding them has zero collision/navmesh effect.
const HIDDEN_DECOR_TYPES = new Set<string>([
  "HavenUnderbrush", "AgriaUnderbrush", "Vines", "AgriaCrops", "PlantsJungle",
  "AiurFoliage", "HavenFoliage", "AgriaFoliage", "ZerusGroundVines", "ZerusBrambles",
  "Bush", "BelShirFoliage", "ZerusPlantClusterSmall",
]);

// Dark-grove pocket: top-right map quarter (large x, small z) gets a bias toward
// bare/dead tree shapes and fallen logs — gives that corner a foreboding character.
// "Top-right" = KTS SC2 layout mapped to (x > half, z < half) in three.js world space.
// The variant swap is purely cosmetic — no collision or server impact.
const GROVE_TREE_TYPES = new Set<string>([
  "HavenTree", "BelShirCurtain", "MarSaraTree", "MarSaraTreeDead",
  "AiurTree", "BelShirTree", "AgriaTree", "TreeHavenNoLOS",
  "ZerusLargeTree", "CharDunesCurtain",
]);
// Bare, dead, and fallen-log shapes used in the dark grove.
const GROVE_VARIANT_POOL: NatureKey[] = [
  "exTree5A", "exTree7A", "exTree8A",
  "exLog1A", "exLog2A", "exTrunk1A",
];
/** True when world position (x, z) falls in the top-right map quarter. */
function inDarkGrove(x: number, z: number): boolean {
  const half = MAP_UNITS / 2;
  return x > half && z < half;
}

// AO halo radius per bucket (in world meters). Trees throw the widest halo
// because their canopy + trunk together block the most ambient light; rocks
// throw a tighter ring; plants/flowers are small enough that AO would just
// look like dark blotches under grass tufts, so they skip.
const TERRAIN_AO_RADIUS: Record<FallbackBucket, number> = {
  doodadTree:  4.5,
  doodadRock:  2.6,
  doodadPlant: 0,    // skip — small doodads don't read as casting AO
  doodadOther: 2.0,
};
const TERRAIN_AO_STRENGTH = 0.50;     // 50% darkening at the doodad's center

// Bake contact-AO halos into the terrain colors around each static doodad. Pairs
// with `bakeBaseAO` on the doodad meshes themselves — together they read as proper
// grounded shadow without SSAO or per-doodad blob decals. Lives here (not terrain.ts)
// because the halo radius/skip decisions key entirely off doodad buckets; it only
// borrows the terrain mesh to write its vertex colors.
export function bakeTerrainContactAO(terrain: THREE.Mesh, objects: ObjectRow[]): void {
  const colors = terrain.geometry.attributes.color as THREE.BufferAttribute | undefined;
  if (!colors) return;

  for (const obj of objects) {
    // Units (incl. the now-roaming mammoth) don't bake a static ground-AO; resource
    // nodes live in resources.ts.
    if (obj.kind === "unit") continue;
    if (RESOURCE_NODE_TYPES.test(obj.type)) continue;

    let radius: number;
    {
      const res = resolveDoodad(obj.type, rowId(obj));
      if (res.kind === "skip") continue;
      radius = TERRAIN_AO_RADIUS[bucketForType(obj.type)];
    }
    if (radius <= 0) continue;

    // Iterate the integer vertex grid inside the AO radius bbox. Terrain verts
    // are placed at integer (col, row) on the 257×257 grid (1u spacing), so the
    // mapping is direct.
    const cx = obj.x;
    const cz = obj.y;
    const minCol = Math.max(0, Math.floor(cx - radius));
    const maxCol = Math.min(VERT_DIM - 1, Math.ceil(cx + radius));
    const minRow = Math.max(0, Math.floor(cz - radius));
    const maxRow = Math.min(VERT_DIM - 1, Math.ceil(cz + radius));
    const r2 = radius * radius;

    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        const dx = col - cx;
        const dz = row - cz;
        const d2 = dx * dx + dz * dz;
        if (d2 >= r2) continue;
        // Smoothstep falloff: full darkening at center, none at edge.
        const t = Math.sqrt(d2) / radius;
        const eased = t * t * (3 - 2 * t);
        const factor = 1 - (1 - eased) * TERRAIN_AO_STRENGTH;
        const idx = row * VERT_DIM + col;
        colors.setXYZ(
          idx,
          colors.getX(idx) * factor,
          colors.getY(idx) * factor,
          colors.getZ(idx) * factor,
        );
      }
    }
  }
  colors.needsUpdate = true;
}

// All authored doodads render as-placed — no overlap culling. (An earlier
// dedupeDoodads pass thinned the map ~50% without fixing the visible overlaps,
// which are harvestable-on-harvestable nodes in the source map; reverted.)

function bucketForType(type: string): FallbackBucket {
  if (/Tree$/i.test(type)) return "doodadTree";
  if (/Rock|Rubble|Stone/i.test(type)) return "doodadRock";
  if (/Crop|Bush|Grass|Plant|Berry|Mushroom|Flower/i.test(type)) return "doodadPlant";
  return "doodadOther";
}

// Stable per-row identifier for variant hashing. (type + position + rot) is unique for
// every KTS map row and survives JSON re-export reorderings. Truncate floats to 2dp so
// floating-point noise doesn't shuffle variants between exports.
function rowId(o: ObjectRow): string {
  return `${o.type}@${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)},${o.rot.toFixed(2)}`;
}

// Quaternius props are single-mesh glTFs but with multiple primitives (bark + leaves on
// trees, etc.) — GLTFLoader emits one THREE.Mesh per primitive. Render every primitive as
// its own InstancedMesh sharing the same per-instance transforms.
// Bull is a SkinnedMesh — InstancedMesh renders its rest pose, fine for a static mammoth.
type Renderable = { geom: THREE.BufferGeometry; mat: THREE.Material; cutout: boolean };

// Doodad materials use toon shading — `toToon()` returns a MeshToonMaterial that
// reads from the shared 3-band gradient ramp (`toon.ts`). Replaces the prior
// flat-shaded Lambert path: toon's discrete L·N banding gives the same chunky
// contrast on Quaternius geometry without the under-lit averaged-normal problem
// that smooth Standard materials had. Downstream tinting clones the toon material
// and is unaffected.

// Only TALL buckets (trees) participate in the see-through cutout. `allowCutout`
// is the bucket gate; `occludesHero` is a secondary height check within it. Short
// buckets (rock/plant/other) pass allowCutout=false so a scaled-up squat boulder
// can't clear the 2.5m height threshold and stipple away — that was hiding
// harvestable-looking grey rocks at close zoom (playtest 2026-06-26). `scale` is
// the group's placement scale.
function extractRenderables(gltf: GLTF, scale: number, allowCutout = true): Renderable[] {
  const out: Renderable[] = [];
  gltf.scene.traverse((node) => {
    const m = node as THREE.Mesh;
    if (!m.isMesh) return;
    const mat = Array.isArray(m.material) ? m.material[0]! : m.material;
    // Bake a soft contact-shadow gradient into vertex colors at the bottom of
    // each primitive's bbox. Idempotent — geometry is shared across instances
    // so all clones of this asset get the AO for free.
    bakeBaseAO(m.geometry);
    const cutout = allowCutout && occludesHero(m.geometry, scale);
    // `fogMask: true` — every static doodad dissolves away in never-explored fog
    // and stipple-dissolves back in once its ground has been scouted (map memory).
    out.push({ geom: m.geometry, mat: toToon(mat, { noiseScale: 2.5, cutout, fogMask: true }), cutout });
  });
  if (out.length === 0) throw new Error("glTF has no Mesh node");
  return out;
}

// Tinting clones the per-renderable material so multiple groups using the same source
// GLTF don't fight over a shared color.
//
// Tint application preserves the source texture map + vertex colors and recolors the
// fragment via a luminance-preserving injection into Lambert's `color_fragment` chunk.
// Each fragment's luminance from the source diffuse drives the tint amplitude, so a
// dark-brown trunk texel ends up as dark-tint and a bright-green leaf texel ends up
// as bright-tint — the trunk/canopy brightness pattern survives, only hue shifts.
// Plain `material.color × tint` would zero the leaf channel for hue-mismatched tints
// (red × green ≈ 0); plain "drop the map" kills the trunk/leaf distinction entirely.
// This middle path keeps the painted look while letting per-instance accent tints
// read clearly under Lambert + ACES.
//
// Emissive-only path keeps the source intact (used for lava-rock glow).
function applyTint(
  mat: THREE.Material,
  tint?: number,
  emissive?: number,
  emissiveIntensity?: number,
  cutout = false,
): THREE.Material {
  if (tint === undefined && emissive === undefined) return mat;
  const cloned = (mat as THREE.MeshToonMaterial).clone();
  if (tint !== undefined) {
    // Compose tint + rim via the shared toon shader. The clone's
    // `onBeforeCompile` (rim from `toToon`) is replaced by the composed hook,
    // so tinted instances still get rim — earlier this was a split-callback
    // setup where the tint callback overrode rim. `cutout` is threaded from the
    // source renderable so tinting doesn't silently re-enable the occlusion
    // fade on a short doodad that opted out.
    applyToonShader(cloned, { tintColor: tint, noiseScale: 2.5, cutout, fogMask: true });
  }
  if (emissive !== undefined) {
    cloned.emissive.setHex(emissive);
    if (emissiveIntensity !== undefined) cloned.emissiveIntensity = emissiveIntensity;
  }
  return cloned;
}

// Group key bundles (variant, tint, emissive) so two registry rows that share a mesh
// but differ in tint render as separate InstancedMesh groups. `null` tints share a
// group with the bucket-fallback path so untinted variants don't fragment.
type GroupResolved = {
  variant: NatureKey;
  scale: number;
  tint?: number;
  emissive?: number;
  emissiveIntensity?: number;
  rotOffset: number;
};

function resolveRow(o: ObjectRow): GroupResolved | "skip" {
  if (o.kind === "unit") {
    if (RESOURCE_NODE_TYPES.test(o.type)) return "skip";
    if (o.type === "TheOldMammoth") return "skip";  // roaming creature now — rendered via the creature path, not a static doodad
    // Other unit kinds (resurrection shrines, etc.) — fall through to doodadOther bucket.
    const fb = FALLBACK_BUCKETS.doodadOther;
    return { variant: fb.variant, scale: fb.scale, rotOffset: 0 };
  }
  // Berry-bush look-alike decor — hidden so only harvestable bushes remain (see
  // HIDDEN_DECOR_TYPES). Restore by clearing that set when distinct art lands.
  if (HIDDEN_DECOR_TYPES.has(o.type)) return "skip";
  const res = resolveDoodad(o.type, rowId(o));
  if (res.kind === "skip") return "skip";
  if (res.kind === "mesh") {
    // Dark-grove override: tree instances in the top-right quarter are swapped to
    // bare/dead shapes deterministically (same hashId salt keeps it stable across
    // reloads). Scale uses the registry entry's scale so sizes stay consistent.
    let variant = res.variant;
    if (GROVE_TREE_TYPES.has(o.type) && inDarkGrove(o.x, o.y)) {
      const pick = hashId(rowId(o) + ":grove") % GROVE_VARIANT_POOL.length;
      variant = GROVE_VARIANT_POOL[pick]!;
    }
    return {
      variant,
      scale: res.entry.scale,
      // Resolution-level tint already folds in entry.tint and any tintPool pick;
      // entry.tint is the per-row default, res.tint may override it per-instance.
      tint: res.tint,
      emissive: res.entry.emissive,
      emissiveIntensity: res.entry.emissiveIntensity,
      rotOffset: res.entry.rotOffset ?? 0,
    };
  }
  // miss: fall through to per-shape bucket
  const fb = FALLBACK_BUCKETS[bucketForType(o.type)];
  return { variant: fb.variant, scale: fb.scale, rotOffset: 0 };
}

// Spatial culling for instanced foliage. The forest is hundreds-to-thousands of
// trees, but a single map-spanning InstancedMesh per variant can never be
// frustum-culled (its bounds always intersect the view), so EVERY tree is
// vertex-shaded each frame even though — at the locked MOBA zoom — only ~1-3% are
// on screen. We instead split each variant's instances into CHUNK_SIZE grid cells:
// each cell becomes its own InstancedMesh with an instance-spanning boundingSphere
// (computeBoundingSphere), so three.js frustum-culls whole off-screen cells before
// they ever reach a draw call. Pure win, no quality change on any GPU — off-screen
// geometry is invisible regardless. Costs a few extra (cheap, cullable) draw groups.
const CHUNK_SIZE = 64; // world units per culling cell

// ── Whole-canopy occluder fade (feature 2, 2026-07-02) ───────────────────────
// Cutout-eligible doodad primitives (bark, leaves, ...) carry a per-instance
// `instanceFade` InstancedBufferAttribute so ONE specific tree/rock instance —
// not its whole InstancedMesh chunk, and not a screen-space circle — can fade
// toward ghost transparency when it's the thing actually standing between the
// camera and the hero. The frame loop (main.ts, via world/canopy-fade.ts)
// raycasts camera→hero each scan tick, finds the hit InstancedMesh+instanceId,
// looks it up here, and writes a lerped 0..1 fade value into the attribute.
//
// `buildGroupMeshes` calls emitChunkedInstances once PER PRIMITIVE (bark, then
// leaves, ...) of a group, all fed the SAME `matrices` array in the SAME order
// — so the cell-bucketing below is deterministic and identical across those
// calls. Passing one shared `fadeAttrs` Map (keyed by cell key) across all of a
// group's cutout-eligible primitive calls means bark and leaves at the same
// original doodad row land at the same cell + instanceId and therefore share
// the SAME InstancedBufferAttribute — they fade in lockstep as "one doodad".
const fadeRegistry = new WeakMap<THREE.InstancedMesh, THREE.InstancedBufferAttribute>();

/** The shared per-instance fade attribute for a doodad InstancedMesh, if it has one (cutout-eligible only). */
export function getDoodadFadeAttribute(mesh: THREE.InstancedMesh): THREE.InstancedBufferAttribute | undefined {
  return fadeRegistry.get(mesh);
}

// A cutout-eligible primitive needs its OWN BufferGeometry object per cell so
// a per-cell `instanceFade` attribute can be attached without leaking onto
// the many other InstancedMesh cells/primitives that share the base geometry
// (see emitChunkedInstances below). `BufferGeometry.clone()` would do this
// too, but it DEEP-copies every attribute's typed array (position/normal/uv/
// index) — real vertex data duplicated per cell for no reason, since the
// vertex data itself never differs between cells. Build a fresh wrapper that
// points at the SAME BufferAttribute objects instead: three.js renders two
// geometries sharing attribute references identically (the attribute, not
// the BufferGeometry wrapper, is the unit of GPU buffer upload), so this is
// free memory-wise and behaviorally identical.
function shareGeometryForFadeCell(geom: THREE.BufferGeometry): THREE.BufferGeometry {
  const out = new THREE.BufferGeometry();
  for (const name in geom.attributes) {
    out.setAttribute(name, geom.attributes[name]!);
  }
  if (geom.index) out.setIndex(geom.index);
  out.groups = geom.groups.map((g) => ({ ...g }));
  if (geom.boundingBox) out.boundingBox = geom.boundingBox.clone();
  if (geom.boundingSphere) out.boundingSphere = geom.boundingSphere.clone();
  return out;
}

// Exported so other doodad-shaped instancers (canyon-rocks.ts) can reuse the
// same chunked-instancing + per-instance fade-attribute wiring instead of a
// second hand-rolled InstancedMesh builder — keeps "cutout-eligible" doodads
// uniform: chunked for frustum culling, and (when fadeAttrs is passed) wired
// into the whole-canopy fade system the same way as forest doodads.
export function emitChunkedInstances(
  root: THREE.Object3D,
  geom: THREE.BufferGeometry,
  mat: THREE.Material,
  matrices: THREE.Matrix4[],
  name: string,
  castShadow: boolean,
  // Present only for cutout-eligible primitives; shared across a group's
  // primitive calls (see comment above) so cell buckets line up.
  fadeAttrs?: Map<string, THREE.InstancedBufferAttribute>,
): void {
  const cells = new Map<string, THREE.Matrix4[]>();
  for (const m of matrices) {
    const cx = Math.floor(m.elements[12] / CHUNK_SIZE);
    const cz = Math.floor(m.elements[14] / CHUNK_SIZE);
    const key = `${cx},${cz}`;
    let arr = cells.get(key);
    if (!arr) { arr = []; cells.set(key, arr); }
    arr.push(m);
  }
  let ci = 0;
  for (const [key, arr] of cells) {
    // Fade-eligible primitives get their own geometry WRAPPER (attributes
    // shared by reference, not deep-copied — see shareGeometryForFadeCell)
    // so attaching a per-cell instanceFade attribute here doesn't leak onto
    // the many other InstancedMesh cells/primitives sharing `geom`.
    const useGeom = fadeAttrs ? shareGeometryForFadeCell(geom) : geom;
    const inst = new THREE.InstancedMesh(useGeom, mat, arr.length);
    inst.name = `${name}#${ci++}`;
    inst.castShadow = castShadow;
    arr.forEach((m, i) => inst.setMatrixAt(i, m));
    inst.instanceMatrix.needsUpdate = true;
    inst.computeBoundingSphere(); // instance-spanning sphere → per-cell frustum cull
    if (fadeAttrs) {
      let attr = fadeAttrs.get(key);
      if (!attr) {
        attr = new THREE.InstancedBufferAttribute(new Float32Array(arr.length), 1);
        attr.setUsage(THREE.DynamicDrawUsage);
        fadeAttrs.set(key, attr);
      }
      useGeom.setAttribute("instanceFade", attr);
      fadeRegistry.set(inst, attr);
    }
    root.add(inst);
  }
}

// Border tree-line (issue #4, 2026-06-18). The playable doodads stop dead at the
// map boundary, so the forest has a visible "tree wall" edge. This scatters pine
// instances OUTWARD over the terrain skirt (world/terrain.ts) — denser near the
// edge, thinning into the fog — so the forest continues past the play area and
// the boundary disappears. Purely decorative + client-only: no collision, no
// server state, no FoW (it sits outside the [0,256] play square). Trees rest on
// the skirt via an edge-CLAMPED height sample (matches the skirt's level
// continuation of the edge heights). Reuses the same pine asset + toon material
// + instancing as the in-map forest so it's visually identical.
const BORDER_DEPTH = 130;  // how far out the tree-line reaches (most fogs out by ~150u)
const BORDER_STEP = 6;     // scatter grid spacing (u)
export function buildBorderTrees(
  sampleHeight: (x: number, z: number) => number,
  assets: AssetBundle,
): THREE.Object3D {
  const root = new THREE.Group();
  root.name = "border-trees";
  const scale = FALLBACK_BUCKETS.doodadTree.scale;
  const renderables = extractRenderables(assets.nature.pine3, scale);
  const M = MAP_UNITS;
  const edgeH = (x: number, z: number) =>
    sampleHeight(Math.max(0, Math.min(M - 0.01, x)), Math.max(0, Math.min(M - 0.01, z)));

  const dummy = new THREE.Object3D();
  const matrices: THREE.Matrix4[] = [];
  for (let gx = -BORDER_DEPTH; gx <= M + BORDER_DEPTH; gx += BORDER_STEP) {
    for (let gz = -BORDER_DEPTH; gz <= M + BORDER_DEPTH; gz += BORDER_STEP) {
      // Skip the play square — real doodads own it.
      if (gx > -2 && gx < M + 2 && gz > -2 && gz < M + 2) continue;
      // Distance from the [0,M] square (0 at the edge → BORDER_DEPTH far out).
      const dxo = gx < 0 ? -gx : gx > M ? gx - M : 0;
      const dzo = gz < 0 ? -gz : gz > M ? gz - M : 0;
      const dist = Math.hypot(dxo, dzo);
      if (dist > BORDER_DEPTH) continue;
      // Denser near the edge, thinning outward (continues the forest, fades out).
      const p = 0.55 * (1 - dist / BORDER_DEPTH);
      if (Math.random() > p) continue;
      const jx = gx + (Math.random() - 0.5) * BORDER_STEP;
      const jz = gz + (Math.random() - 0.5) * BORDER_STEP;
      const s = scale * (0.8 + Math.random() * 0.5);
      // Seat on the skirt: the collar stays level out to ~110u then tapers down,
      // so past that the flat edge height floats above the ground. Subtract the
      // skirt's drop at this distance so trees rest on the collar, not above it.
      dummy.position.set(jx, edgeH(jx, jz) - skirtDrop(dist), jz);
      dummy.rotation.set(0, Math.random() * Math.PI * 2, 0);
      dummy.scale.set(s, s, s);
      dummy.updateMatrix();
      matrices.push(dummy.matrix.clone());
    }
  }

  const fadeAttrs = new Map<string, THREE.InstancedBufferAttribute>();
  renderables.forEach((r, ri) => {
    // castShadow false: distant + fogged — shadows just churn the shadow map.
    emitChunkedInstances(root, r.geom, r.mat, matrices, `border-trees:${ri}`, false, r.cutout ? fadeAttrs : undefined);
  });
  return root;
}

export function buildObjects(
  objects: ObjectRow[],
  sampleHeight: (x: number, z: number) => number,
  assets: AssetBundle,
): THREE.Object3D {
  const root = new THREE.Group();
  root.name = "objects";

  // Group by (variant + tint signature) so each group becomes one set of
  // InstancedMesh primitives. Tint is part of the key because tinted
  // entries clone the material; sharing a group with untinted instances
  // would force them all to inherit the tint.
  type GroupBucket = {
    resolved: GroupResolved;
    rows: ObjectRow[];
  };
  const groups = new Map<string, GroupBucket>();

  for (const o of objects) {
    const r = resolveRow(o);
    if (r === "skip") continue;
    const key = `${r.variant}|${r.tint ?? "_"}|${r.emissive ?? "_"}`;
    let g = groups.get(key);
    if (!g) {
      g = { resolved: r, rows: [] };
      groups.set(key, g);
    }
    g.rows.push(o);
  }

  const dummy = new THREE.Object3D();

  const buildGroupMeshes = (
    label: string,
    gltf: GLTF,
    rows: ObjectRow[],
    scale: number,
    rotOffset: number,
    castShadow: boolean,
    allowCutout: boolean,
    tint?: number,
    emissive?: number,
    emissiveIntensity?: number,
  ) => {
    // `allowCutout` is the bucket gate; `occludesHero` (height > 2.5m) is the
    // per-mesh check inside extractRenderables. Decoupled from `castShadow`
    // (shadows stay tree-only for perf) so TALL ROCKS / props the hero can stand
    // behind also fade — previously the whole rock bucket was hard-excluded, so a
    // big rock spire never went see-through even with the player right under it
    // (Aviv occlusion-bug, 2026-06-29). Short rocks/pebbles still opt out via the
    // height gate, preserving the 2026-06-26 "don't stipple harvestable-looking
    // grey rocks" fix.
    const renderables = extractRenderables(gltf, scale, allowCutout);
    // Shared across every primitive of THIS group's rows so cell buckets align
    // (see the fadeRegistry comment above emitChunkedInstances).
    const fadeAttrs = new Map<string, THREE.InstancedBufferAttribute>();
    const matrices: THREE.Matrix4[] = rows.map((o) => {
      // SC2 (x, y) ground -> three.js (x, z); SC2 z (height) -> three.js y.
      // Always sample the heightmap: ObjectUnit z is 0 in the XML; doodad z values
      // are within ~0.5 of terrain on flat ground but drift on slopes (the SC2
      // placement editor and our exported heightmap diverge enough to make trees
      // visibly hover, up to ~4m). Sampling locks every base to the current surface.
      const groundY = sampleHeight(o.x, o.y);
      const s = (o.scale || 1) * scale;
      dummy.position.set(o.x, groundY, o.y);
      dummy.rotation.set(0, -o.rot + rotOffset, 0);
      dummy.scale.set(s, s, s);
      dummy.updateMatrix();
      return dummy.matrix.clone();
    });
    renderables.forEach((r, ri) => {
      const mat = applyTint(r.mat, tint, emissive, emissiveIntensity, r.cutout);
      // Plants (grass, mushrooms, ferns, flowers) and small rocks (pebbles) skip
      // shadows — they shimmer at this map size and read as noise (`14-rendering.md`).
      // Only larger doodads (trees, big rocks, mammoth) cast. Chunked for per-cell
      // frustum culling (see emitChunkedInstances).
      emitChunkedInstances(root, r.geom, mat, matrices, `objects:${label}:${ri}`, castShadow, r.cutout ? fadeAttrs : undefined);
    });
  };

  for (const [key, g] of groups) {
    const gltf = assets.nature[g.resolved.variant];
    // First row's KTS type drives the shadow bucket — all rows in a group share
    // a variant, and bucketForType is purely string-pattern based, so any row works.
    const bucket = bucketForType(g.rows[0]!.type);
    const castShadow = bucket === "doodadTree";
    // Trees, rocks, and misc tall props can fade; ground plants (grass/ferns/
    // flowers) never occlude the hero, so they skip the cutout entirely.
    const allowCutout = bucket !== "doodadPlant";
    buildGroupMeshes(
      key,
      gltf,
      g.rows,
      g.resolved.scale,
      g.resolved.rotOffset,
      castShadow,
      allowCutout,
      g.resolved.tint,
      g.resolved.emissive,
      g.resolved.emissiveIntensity,
    );
  }

  // (Mammoth is now roaming wild megafauna — rendered via the creature path, no static doodad.)

  return root;
}
